import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  UsersIcon,
  HouseIcon,
  WrenchIcon,
  ClipboardTextIcon,
  PackageIcon,
  XCircleIcon,
} from "@phosphor-icons/react";

import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  countResults,
  fetchGlobalSearch,
  pathFor,
} from "../../lib/search";

const ICONS = {
  customers: UsersIcon,
  properties: HouseIcon,
  work_orders: WrenchIcon,
  service_requests: ClipboardTextIcon,
  rma_records: PackageIcon,
};

/**
 * Topbar global search. Owner + ops only.
 *
 * - `/` keypress focuses the input (when no other input is focused).
 * - 300ms debounce on typing.
 * - Esc closes the dropdown and blurs.
 * - Click result → navigate.
 * - Click outside → closes.
 */
export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const nav = useNavigate();

  // Debounced fetch.
  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await fetchGlobalSearch(trimmed);
        setResults(data);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  // `/` keyboard shortcut focuses search.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = document.activeElement?.tagName;
        const inField =
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          document.activeElement?.isContentEditable;
        if (inField) return;
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Outside-click closes dropdown.
  useEffect(() => {
    const onClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const total = useMemo(() => countResults(results), [results]);
  const showDropdown = open && q.trim().length >= 2;

  const onPick = (cat, id) => {
    nav(pathFor(cat, id));
    setOpen(false);
    setQ("");
    inputRef.current?.blur();
  };

  return (
    <div
      ref={wrapperRef}
      className="relative w-full max-w-md"
      data-testid="global-search"
    >
      <MagnifyingGlassIcon
        size={14}
        weight="bold"
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
      />
      <input
        ref={inputRef}
        type="search"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => q.trim().length >= 2 && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
            inputRef.current?.blur();
          }
        }}
        placeholder="Search customers, properties, jobs… (press /)"
        data-testid="global-search-input"
        className="h-9 w-full rounded-md border border-slate-200 bg-slate-50/70 pl-8 pr-9 text-sm text-slate-900 placeholder:text-slate-400 focus:border-amber-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20"
      />
      {q && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setQ("");
            setResults(null);
            inputRef.current?.focus();
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          aria-label="Clear search"
          data-testid="global-search-clear"
        >
          <XCircleIcon size={14} weight="fill" />
        </button>
      )}

      {showDropdown && (
        <div
          data-testid="global-search-dropdown"
          className="absolute left-0 right-0 z-50 mt-1.5 max-h-[70vh] overflow-y-auto rounded-md border border-slate-200 bg-white shadow-xl"
        >
          {loading && (
            <div className="px-4 py-3 text-xs text-slate-500">Searching…</div>
          )}
          {!loading && total === 0 && results && (
            <div
              data-testid="global-search-empty"
              className="px-4 py-6 text-center text-sm text-slate-500"
            >
              No results for &ldquo;{q.trim()}&rdquo;
            </div>
          )}
          {!loading &&
            total > 0 &&
            CATEGORY_ORDER.map((cat) => {
              const rows = results?.[cat] || [];
              if (rows.length === 0) return null;
              const Icon = ICONS[cat];
              return (
                <div key={cat} className="border-b border-slate-100 last:border-b-0">
                  <div className="font-data px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    {CATEGORY_LABELS[cat]}
                  </div>
                  <ul>
                    {rows.map((row) => (
                      <li key={`${cat}-${row.id}`}>
                        <button
                          type="button"
                          onClick={() => onPick(cat, row.id)}
                          data-testid={`global-search-result-${cat}-${row.id}`}
                          className="flex w-full items-center gap-3 px-4 py-2 text-left transition hover:bg-amber-50"
                        >
                          <Icon
                            size={14}
                            weight="bold"
                            className="shrink-0 text-slate-400"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-slate-900">
                              {row.label}
                            </span>
                            {row.sublabel && (
                              <span className="block truncate text-xs text-slate-500">
                                {row.sublabel}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
